import { useState } from "react";
import { useSettings } from "../lib/settings";
import { api, formatApiError } from "../lib/api";
import { Sparkles, Download, Mail, CheckCircle2 } from "lucide-react";

export default function LeadMagnet() {
  const { settings } = useSettings();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.post("/lead-magnet", { email });
      setDone(data);
    } catch (er) {
      setErr(formatApiError(er.response?.data?.detail) || "Couldn't subscribe.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section data-testid="lead-magnet" className="bg-[#2C2924] text-[#FDFBF7] relative overflow-hidden">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24 relative z-10">
        <div className="rounded-3xl p-10 md:p-14 text-center" style={{
          border: "4px solid var(--accent)",
          boxShadow: "0 0 0 1px rgba(255,255,255,0.06), 0 30px 60px -30px var(--accent)",
        }}>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[var(--accent)]/15 text-[var(--accent)] uppercase text-[11px] tracking-[0.35em]">
            <Sparkles className="w-3.5 h-3.5" /> Free for you
          </div>
          <h2 className="mt-5 font-serif text-4xl md:text-5xl gold-heading">
            {settings.lead_magnet_title || "5 free Nano Banana prompts"}
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-[#F3E5AB]/80 leading-relaxed">
            Drop your email and I'll send 5 of my favorite, hand-tested prompts straight to your inbox.
            No spam — only the good stuff.
          </p>
          {done ? (
            <div className="mt-8 inline-flex flex-col items-center gap-3" data-testid="lead-magnet-success">
              <CheckCircle2 className="w-10 h-10 text-[var(--accent)]" />
              <div className="font-serif text-2xl">Welcome — your prompts are ready.</div>
              <a href={done.download_url} target="_blank" rel="noreferrer" data-testid="lead-magnet-download"
                className="mt-2 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[var(--accent)] text-black hover:opacity-90 text-sm font-semibold">
                <Download className="w-4 h-4" /> Download {done.title}
              </a>
              <p className="text-xs text-[#F3E5AB]/70 mt-2">Also sent to {email}.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row max-w-lg mx-auto gap-3">
              <div className="flex-1 relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F3E5AB]/60" />
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="lead-magnet-email"
                  className="w-full pl-11 pr-4 py-3.5 rounded-full bg-[#1A1815] border-2 border-[var(--accent)]/40 focus:border-[var(--accent)] focus:outline-none text-sm text-[#FDFBF7] placeholder:text-[#F3E5AB]/40" />
              </div>
              <button type="submit" disabled={loading} data-testid="lead-magnet-submit"
                className="px-7 py-3.5 rounded-full bg-[var(--accent)] text-black hover:opacity-90 font-semibold text-sm disabled:opacity-60">
                {loading ? "Sending…" : "Send my 5 prompts"}
              </button>
            </form>
          )}
          {err && <div className="mt-3 text-sm text-red-300">{err}</div>}
        </div>
      </div>
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[var(--accent)]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-[var(--accent)]/10 blur-3xl pointer-events-none" />
    </section>
  );
}
